import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import T from 'components/common/text/T';
import { color } from 'styles/utils';
import MuiButton from 'components/common/button/MuiButton';
import CustomSpan from 'components/common/text/CustomSpan';
import { StyledPlainButtonOuter } from 'components/common/styled/Button';
import cx from 'classnames';
import { useShallowAppSelector } from 'store/hooks';
import { UserActions } from 'store/actionCreators';
import { useDidUpdateEffect } from 'lib/utils';
import MuiWrapper from 'components/common/input/MuiWrapper';
import { TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import IconButton from '@material-ui/core/IconButton';
import { icon_plus_gray } from 'components/base/images';
import CloseIcon from '@material-ui/icons/Close';
import PencilUnderlineIcon from 'components/base/icons/PencilUnderlineIcon';
import useInput from 'lib/hooks/useInput';
import { validInputString } from 'lib/library';

interface UserCustomObjectModalProps {
  type: string;
  onCloseModal: () => void;
}

interface UserCustomObjectListItemProps {
  item: any;
  modifyIdx: {
    value: string;
    onChange: (e: any) => void;
    setValue: (value: string) => void;
  };
  currObjIdx: {
    value: string;
    onChange: (e: any) => void;
    setValue: (value: string) => void;
  };
  onModifyObject: (idx: string, name: string) => void;
  onDeleteObject: (idx: string) => void;
}

function UserCustomObjectModalContainer({ type, onCloseModal }: UserCustomObjectModalProps) {
  const {
    fetchUserIndicationList: { list: userIndicationList },
    fetchUserIndicationListSuccess,
    updateUserIndicationSuccess,
    deleteUserIndicationSuccess,
    fetchUserMaterialList: { list: userMaterialList },
    fetchUserMaterialListSuccess,
    updateUserMaterialSuccess,
    deleteUserMaterialSuccess,
  }: {
    fetchUserIndicationList: { list: any[] };
    fetchUserIndicationListSuccess: boolean;
    updateUserIndicationSuccess: boolean;
    deleteUserIndicationSuccess: boolean;
    fetchUserMaterialList: { list: any[] };
    fetchUserMaterialListSuccess: boolean;
    updateUserMaterialSuccess: boolean;
    deleteUserMaterialSuccess: boolean;
  } = useShallowAppSelector(state => ({
    fetchUserIndicationList: state.user.fetchUserIndicationList.data || {},
    fetchUserIndicationListSuccess: state.user.fetchUserIndicationList.success,
    updateUserIndicationSuccess: state.user.updateUserIndication.success,
    deleteUserIndicationSuccess: state.user.deleteUserIndication.success,
    fetchUserMaterialList: state.user.fetchUserMaterialList.data || {},
    fetchUserMaterialListSuccess: state.user.fetchUserMaterialList.success,
    updateUserMaterialSuccess: state.user.updateUserMaterial.success,
    deleteUserMaterialSuccess: state.user.deleteUserMaterial.success,
  }));

  const { ipcRenderer } = window;
  const MAX_COUNT = 30;
  const INDICATION = 'INDICATION';
  const MATERIAL = 'MATERIAL';
  const { t } = useTranslation();

  const modifyIdx = useInput('');
  const [isUnaddition, setIsUnaddition] = useState<boolean>(false);

  const [newObject, setNewObject] = useState<string>('');

  const [objListCt, setObjListCt] = useState<Object[]>([]);
  const [objListChangeCt, setObjListChangeCt] = useState<Object[]>([]);

  const currObjIdx = useInput('');
  useEffect(() => {
    if (type === INDICATION) {
      UserActions.fetch_user_indication_list_request();
      return () => {
        UserActions.fetch_user_indication_list_clear();
      };
    }

    if (type === MATERIAL) {
      UserActions.fetch_user_material_list_request();
      return () => {
        UserActions.fetch_user_material_list_clear();
      };
    }
  }, [type]);

  const updateOfflineIndicationInfo = () => {
    if (!ipcRenderer) return;
    ipcRenderer.send('setIndicationInfoList');
  };

  useDidUpdateEffect(() => {
    if (fetchUserIndicationListSuccess) {
      setObjListCt([...userIndicationList]);
      setObjListChangeCt([...userIndicationList]);
    }
  }, [fetchUserIndicationListSuccess]);

  useDidUpdateEffect(() => {
    if (fetchUserMaterialListSuccess) {
      setObjListCt([...userMaterialList]);
      setObjListChangeCt([...userMaterialList]);
    }
  }, [fetchUserMaterialListSuccess]);

  useDidUpdateEffect(() => {
    if (updateUserIndicationSuccess) {
      updateOfflineIndicationInfo();
      UserActions.fetch_user_indication_list_request();
      if (!!currObjIdx.value) {
        modifyIdx.setValue(currObjIdx.value);
      } else {
        setNewObject('');
      }
    }
  }, [updateUserIndicationSuccess, currObjIdx.value]);

  useDidUpdateEffect(() => {
    if (updateUserMaterialSuccess) {
      updateOfflineIndicationInfo();
      UserActions.fetch_user_material_list_request();
      if (!!currObjIdx.value) {
        modifyIdx.setValue(currObjIdx.value);
      } else {
        setNewObject('');
      }
    }
  }, [updateUserMaterialSuccess, currObjIdx.value]);

  useDidUpdateEffect(() => {
    if (deleteUserIndicationSuccess) {
      updateOfflineIndicationInfo();
      UserActions.fetch_user_indication_list_request();
      // TODO: 제거로직
    }
  }, [deleteUserIndicationSuccess]);

  useDidUpdateEffect(() => {
    if (deleteUserMaterialSuccess) {
      updateOfflineIndicationInfo();
      UserActions.fetch_user_material_list_request();
      // TODO: 제거로직
    }
  }, [deleteUserMaterialSuccess]);

  useEffect(() => {
    if (objListCt.length < MAX_COUNT) {
      setIsUnaddition(false);
    } else {
      setIsUnaddition(true);
    }
  }, [objListCt]);

  const handleUpdateObject = (idx: string, name: string) => {
    currObjIdx.setValue(idx);
    if (type == INDICATION) {
      const submitData = {
        editIndicationIdx: !!idx ? idx : null,
        indicationName: name.trim(),
      };
      UserActions.update_user_indication_request(submitData);
    }

    if (type == MATERIAL) {
      const submitData = {
        editMaterialIdx: !!idx ? idx : null,
        materialName: name.trim(),
      };
      UserActions.update_user_material_request(submitData);
    }
  };
  const handleDeleteObject = (idx: string) => {
    if (type == INDICATION) {
      const submitData = {
        indicationIdx: idx,
      };
      UserActions.delete_user_indication_request(submitData);
    }

    if (type == MATERIAL) {
      const submitData = {
        materialIdx: idx,
      };
      UserActions.delete_user_material_request(submitData);
    }
  };

  return (
    <UserCustomObjectModal data-component-name="UserCustomObjectModal">
      <div className="userCustomObjectModal__container">
        <div className="userCustomObjectModal__comment">
          <p>{t(`USER_${type}_DETAIL`)}</p>
          {!!isUnaddition && (
            <CustomSpan fontSize={13} fontColor="#f00f00">
              {'* '}
              <T>GLOBAL_UNADDABLE_COUNT</T>
            </CustomSpan>
          )}
        </div>

        <div className="userCustomObjectModal__object_add_wrapper">
          <div
            className={cx('userCustomObjectModal__object_add_label', {
              isUnaddition,
            })}
          >
            <img src={icon_plus_gray} /> {t(`USER_${type}_NAME`)}
          </div>
          <div className="userCustomObjectModal__object_add_box">
            <MuiWrapper>
              <TextField
                className="userCustomObjectModal__object_add"
                variant="outlined"
                fullWidth
                type="text"
                placeholder={t(`USER_${type}_PLACEHOLDER`)}
                disabled={isUnaddition}
                value={newObject}
                onChange={e => {
                  const checkText = validInputString(e.target.value);
                  if (checkText.length > 20) return;
                  if (checkText.length == 1) {
                    setNewObject(checkText.trim());
                  } else {
                    setNewObject(checkText);
                  }
                }}
              />
            </MuiWrapper>
            <MuiButton
              className="userCustomObjectModal__object_add_button"
              variant="contained"
              color="default"
              disableElevation
              disabled={isUnaddition || newObject.length < 1}
              onClick={() => {
                handleUpdateObject('', newObject);
              }}
            >
              <CustomSpan fontSize={14}>
                <T>GLOBAL_ADD</T>
              </CustomSpan>
            </MuiButton>
          </div>
        </div>

        <div className="userCustomObjectModal__table_wrapper">
          <div className="userCustomObjectModal__table_box">
            <div className="userCustomObjectModal__table">
              {objListCt.map(
                (item: any) => {
                  return (
                    <div className="userCustomObjectModal__table_item" key={item.idx}>
                      <div className="userCustomObject_contents">
                        <UserCustomObjectListItemComponent
                          item={item}
                          modifyIdx={modifyIdx}
                          currObjIdx={currObjIdx}
                          onModifyObject={handleUpdateObject}
                          onDeleteObject={handleDeleteObject}
                        />
                      </div>
                    </div>
                  );
                },
                [modifyIdx.value],
              )}
            </div>
          </div>
          <div className="userCustomObjectModal__count_box">
            <div className="userCustomObjectModal__count_label">
              <T>GLOBAL_COUNT</T>:
            </div>
            <div className="userCustomObjectModal__count">
              <CustomSpan fontColor="#00a6e2">{objListCt.length}</CustomSpan>
              <CustomSpan fontColor="#444444">{`/${MAX_COUNT}`}</CustomSpan>
            </div>
          </div>
        </div>
        <StyledPlainButtonOuter
          backgroundColor={color.navy_blue}
          left="50%"
          width={370}
          height={76}
        >
          <MuiButton
            type="submit"
            config={{
              width: '320px',
            }}
            disableElevation
            color="primary"
            variant="contained"
            disablebackground="#17288a"
            disablefontcolor="#ffffff"
            className="xl border-radius-round userCustomObjectModal__modify_btn"
            onClick={() => {
              onCloseModal();
            }}
          >
            <CustomSpan fontSize={16}>
              <T>GLOBAL_CONFIRM</T>
            </CustomSpan>
          </MuiButton>
        </StyledPlainButtonOuter>
      </div>
    </UserCustomObjectModal>
  );
}

function UserCustomObjectListItemComponent({
  item,
  modifyIdx,
  currObjIdx,
  onModifyObject,
  onDeleteObject,
}: UserCustomObjectListItemProps) {
  const [itemName, setItemName] = useState<string>(!!item.name ? item.name : '');
  const [prevItemName, setPrevItemName] = useState<string>(!!item.name ? item.name : '');
  const [isModify, setIsModify] = useState<boolean>(false);

  useEffect(() => {
    if (!!isModify && modifyIdx.value == item.idx) {
      setIsModify(false);
      modifyIdx.setValue('');
      currObjIdx.setValue('');
    }
  }, [modifyIdx.value, isModify]);

  return (
    <UserCustomObjectListItem data-component-name="UserCustomObjectListItem">
      {useMemo(
        () => (
          <div
            className={cx('userCustomObjectListItem__wrapper', {
              isModify,
            })}
          >
            <div className="userCustomObjectListItem__content_box">
              {!isModify ? (
                <CustomSpan className="userCustomObjectListItem__input_box " fontSize={14}>
                  {itemName}
                </CustomSpan>
              ) : (
                <MuiWrapper className="userCustomObjectListItem__input_box modify">
                  <TextField
                    className="radius-md userCustomObjectListItem__input"
                    variant="outlined"
                    fullWidth
                    type="text"
                    placeholder="Fill in the remake detail."
                    value={itemName || ''}
                    onChange={e => {
                      const checkText = validInputString(e.target.value);
                      if (checkText.length > 20) return;
                      if (checkText.length == 1) {
                        setItemName(checkText.trim());
                      } else {
                        setItemName(checkText);
                      }
                    }}
                  />
                </MuiWrapper>
              )}
            </div>
            <div className="userCustomObjectListItem__btn_box">
              {!isModify ? (
                <>
                  <IconButton
                    aria-label="delete item"
                    className="userCustomObjectListItem__btn modify"
                    onClick={() => {
                      setIsModify(true);
                    }}
                  >
                    <PencilUnderlineIcon color={color.blue} width={24} />
                  </IconButton>

                  <IconButton
                    aria-label="delete item"
                    className="userCustomObjectListItem__btn delete"
                    onClick={() => {
                      onDeleteObject(item.idx);
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </>
              ) : (
                <>
                  <MuiButton
                    className="userCustomObjectListItem__btn save"
                    variant="contained"
                    color="default"
                    disableElevation
                    onClick={() => {
                      onModifyObject(item.idx, itemName);
                    }}
                  >
                    <CustomSpan fontSize={14}>
                      <T>GLOBAL_SAVE</T>
                    </CustomSpan>
                  </MuiButton>
                  <MuiWrapper className="cancel__btn_box">
                    <MuiButton
                      className="userCustomObjectListItem__btn cancel"
                      variant="outlined"
                      color="default"
                      disableElevation
                      onClick={() => {
                        setIsModify(false);
                        setItemName(prevItemName);
                      }}
                    >
                      <CustomSpan fontSize={14}>
                        <T>GLOBAL_CANCEL</T>
                      </CustomSpan>
                    </MuiButton>
                  </MuiWrapper>
                </>
              )}
            </div>
          </div>
        ),
        [isModify, itemName],
      )}
    </UserCustomObjectListItem>
  );
}

const UserCustomObjectModal = styled.section<{}>`
  .userCustomObjectModal__container {
    width: 100%;
    border-radius: 15px;
    padding: 50px 30px 38px;
    background-color: #ffffff;
    position: relative;
    .userCustomObjectModal__comment {
      margin-bottom: 30px;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      p {
        font-size: 19px;
        font-weight: 700;
        color: #303030;
      }
    }

    .userCustomObjectModal__object_add_wrapper {
      height: 100px;
      background-color: #f9f9fb;
      margin-bottom: 20px;
      padding: 0 35px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      .userCustomObjectModal__object_add_label {
        display: flex;
        align-items: center;
        column-gap: 10px;
        font-size: 14px;
        color: #888;
        &.isUnaddition {
          color: #d1d1d1;
        }
      }
      .userCustomObjectModal__object_add_box {
        display: flex;
        justify-content: space-between;
        .userCustomObjectModal__object_add {
          width: 567px;
          height: 46px;
        }
        .userCustomObjectModal__object_add_button {
          width: 133px;
          height: 46px;
        }
      }
    }
    .userCustomObjectModal__table_wrapper {
      position: relative;
      .userCustomObjectModal__table_box {
        margin-bottom: 80px;
        height: 390px;
        overflow-y: overlay;
        padding-right: 20px;
        margin-right: -20px;
      }
      .userCustomObjectModal__table {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        row-gap: 10px;

        .userCustomObjectModal__table_item {
          width: 455px;
          height: 90px;
          border: 1px solid #dddee4;
          border-radius: 5px;
          display: flex;
          align-items: center;
          .userCustomObject_contents {
            width: 100%;
          }
        }
      }
      .userCustomObjectModal__count_box {
        position: absolute;
        display: flex;
        column-gap: 10px;
        bottom: -36px;
        right: 0;
        align-items: center;
        line-height: 29px;
        .userCustomObjectModal__count_label {
          font-size: 14px;
          padding-top: 3px;
        }
        .userCustomObjectModal__count {
          font-size: 24px;
        }
      }
    }
  }
`;

const UserCustomObjectListItem = styled.div<{}>`
  .userCustomObjectListItem__wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    /* column-gap: 6px; */
    width: 100%;
    padding: 0 25px 0 30px;
    &.isModify {
      padding: 0 25px;
    }
  }

  .userCustomObjectListItem__content_box {
    .userCustomObjectListItem__input_box {
      height: 34px;

      .userCustomObjectListItem__input {
        width: 247px;
        font-size: 13px;
        height: 34px;
      }
    }
    .userCustomObjectListItem__delete {
      width: 78px;
      height: 34px;
      border-radius: 2px;
      color: #888;
      border: 1px solid #d1d1d1;
      font-size: 14px;
    }
  }
  .userCustomObjectListItem__btn_box {
    .userCustomObjectListItem__btn {
      font-size: 24px;
      text-transform: none;
      &.save,
      &.cancel {
        width: 70px;
        height: 40px;
      }
      &.save {
        margin-right: 5px;
      }
    }
  }
`;
export default React.memo(UserCustomObjectModalContainer);
